import { useCallback } from 'react';
import keyBy from 'lodash/keyBy';

import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import { QuestionSubTypeEnum, QuestionTypeEnum } from '@/types/schema';

import { OptionsListItemTypeEnum } from '../../single-select/types';
import { getOptionListItemType } from '../../single-select/utils';
import { QuestionReviewProps } from '../../types';
import { FIRST_KEYCODE_INDEX } from '../Player/OptionsListItem/consts';
import { renderAttachments } from '../Player/OptionsListItem/renderers';
import ReviewIcon from './ReviewIcon';

const QuestionReview: React.FC<QuestionReviewProps> = ({
  question,
  correctAnswers,
  questionAttempt,
}) => {
  const { t } = useTranslation();

  const options = question.options;
  const optionResponse = questionAttempt?.submission?.optionResponse;
  const selectedOptionsById = keyBy(optionResponse || [], 'optionId');
  const isQuestionSubmitted = Boolean(questionAttempt?.isSubmitted);
  const correctOptionsById = keyBy(correctAnswers?.correctOptions, 'optionId');

  const isTrueFalseQuestion = question.questionType === QuestionTypeEnum.TrueOrFalse;
  const isWeightedOption = question.subType === QuestionSubTypeEnum.WeightedOption;

  const getKeyCode = useCallback(
    (index: number, option: { optionText: string }) => {
      if (isTrueFalseQuestion) {
        return option.optionText.charAt(0);
      }
      return String.fromCharCode(FIRST_KEYCODE_INDEX + index);
    },
    [isTrueFalseQuestion],
  );

  if (!isQuestionSubmitted && !optionResponse?.length) {
    return (
      <Typography className="ms-8" color="text-text-placeholder" variant="label-02">
        {t('QuizPlayer:NotAnswered')}
      </Typography>
    );
  }

  return (
    <>
      {options?.map((option, index) => {
        const type = getOptionListItemType({
          correctOptionsById,
          isQuestionSubmitted,
          option,
          selectedOptionsById,
          isWeightedOption,
          options,
        });

        if (type === OptionsListItemTypeEnum.Default) {
          return null;
        }

        const isPartiallyCorrectType =
          type === OptionsListItemTypeEnum.PartiallyCorrect ||
          type === OptionsListItemTypeEnum.PartiallyCorrectMissed;

        return (
          <Stack key={option.optionId} direction="col" gapY={12}>
            {renderAttachments({ attachments: option.attachments })}
            <Stack className="flex-wrap px-8" gapX={12}>
              <ReviewIcon keyCode={getKeyCode(index, option)} type={type} />
              <RichTextViewer
                color="text-text-paragraph"
                containerClassName="min-w-0 flex-1"
                html={option.optionText}
                variant="body-short-02"
              />
              {isQuestionSubmitted && type === OptionsListItemTypeEnum.Missed ? (
                <Typography
                  className="basis-full text-end"
                  color="text-text-success"
                  variant="body-short-02">
                  {t('QuizPlayer:MissedCorrect')}
                </Typography>
              ) : null}
              {isQuestionSubmitted && type === OptionsListItemTypeEnum.Correct ? (
                <Typography
                  className="basis-full text-end"
                  color="text-text-success"
                  variant="body-short-02">
                  {t('QuizPlayer:Correct')}
                </Typography>
              ) : null}
              {isQuestionSubmitted && isPartiallyCorrectType ? (
                <Typography
                  className="basis-full text-end"
                  color="text-text-warning"
                  variant="body-short-02">
                  {t('QuizPlayer:PartiallyCorrect', {
                    percentage: option.optionRelativeScore ?? 0,
                  })}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        );
      })}
    </>
  );
};

export default QuestionReview;
